import { useCallback, useRef } from 'react';
import { useSelector } from 'react-redux';

import { useCollapsible, useResizeState } from 'Core/hooks';
import { fitmentTableResponseSelector } from 'Core/selectors/fitmentSearch/index.js';
import { FitmentFieldValues } from 'Modules/serverApi/types.ts';
import { cloneSafe } from 'Utils/components';
import t from 'Utils/translate';
import VehicleSelector from './vehicleSelector';

import type { FC } from 'react';
import type {
  RepeaterFunctionInvoker,
  TemplateFunction,
  TemplateFunctionInvoker,
  TemplateResult,
} from 'Components/types.ts';
import type { ServerModel } from 'Modules/serverApi/types.ts';
import type { Params as VehicleSelectorParams } from './vehicleSelector';

enum layoutType {
  horizontal = 'horizontal',
  vertical = 'vertical',
}

type FitmentFieldType = {
  vehicleSpecific: boolean;
  noFitmentData: boolean;
  universalFit: boolean;
  manyFitments: boolean;
};

interface ItemParams {
  fitment: ServerModel.GenericItem[];
  vehicleSelector?: TemplateFunctionInvoker<VehicleSelectorParams> | null;
}

type Params = {
  fields: string[];
  fieldTitles: Record<string, string>;
  items: RepeaterFunctionInvoker<ItemParams>;
  alwaysVisibleValues: RepeaterFunctionInvoker<ItemParams>;
  initiallyHiddenValues: RepeaterFunctionInvoker<ItemParams>;
  template: keyof typeof layoutType;
  isCollapsed: boolean;
  toggleCollapsed: () => void;
  valuesCount: number;
} & FitmentFieldType;

interface Props {
  template: TemplateFunction<Params | FitmentFieldType>;
  columnBreakpoint?: number;
}

const FitmentTable: FC<Props> = ({ template, columnBreakpoint = 425 }) => {
  const rootRef = useRef<HTMLElement>(null);

  const layout = useResizeState(
    useCallback(
      () => (window.innerWidth > columnBreakpoint ? layoutType.horizontal : layoutType.vertical),
      [columnBreakpoint],
    ),
  ) as keyof typeof layoutType;

  const [isCollapsed, toggleCollapsed] = useCollapsible(rootRef, null, true);

  const response = useSelector(fitmentTableResponseSelector) as ServerModel.FitmentsResponseType;

  if (
    !response ||
    (response.Fitment === FitmentFieldValues.VehicleSpecific &&
      (!response.Fitments?.length || !response.Fields?.length))
  ) {
    return null;
  }

  const { Fields, Fitment, Fitments } = response;

  const fitmentFieldData = {
    vehicleSpecific: Fitment === FitmentFieldValues.VehicleSpecific,
    noFitmentData: Fitment === FitmentFieldValues.NoFitmentData,
    universalFit: Fitment === FitmentFieldValues.UniversalFit,
    manyFitments: Fitment === FitmentFieldValues.ManyFitments,
  };

  if (!Fields?.length || !Fitments?.length) {
    return template.call({ ...fitmentFieldData });
  }

  const vehicleSelectors = Fitments.some(
    (f) => f.Year && typeof f.Year === 'object' && !Array.isArray(f.Year),
  )
    ? (Fitments.map(
        (fitment) => (templ) =>
          (
            <VehicleSelector template={templ} years={fitment.Year as Record<string, string>} />
          ) as TemplateResult,
      ) as TemplateFunctionInvoker<VehicleSelectorParams>[] | undefined)
    : null;

  const alwaysShowedCount = layout === layoutType.horizontal ? 21 : 11;

  const translatedFields = Fields.map((f) => ({
    ...f,
    Title: t(`facet_title_${f.Name}`) || f.Title,
  }));

  const items = Fitments.map((fitment, i: number) => (templ: TemplateFunction<ItemParams>) => {
    const component = templ.call({
      fitment: translatedFields.reduce((fitmentTemplate, { Name, Title }) => {
        fitmentTemplate[Title] = fitment[Name];
        return fitmentTemplate;
      }, []),
      vehicleSelector: vehicleSelectors ? vehicleSelectors[i] : null,
    });

    // const key = "Year|2015&Make|Ford&Model|F-150"
    const key = Object.entries(fitment)
      .map(([field, value]) => `${field}|${value}`)
      .join('&');

    return cloneSafe(component, null, { key });
  });

  return cloneSafe(
    template.call({
      fields: translatedFields.map((f) => f.Title),
      items,
      alwaysVisibleValues: items.slice(0, alwaysShowedCount),
      initiallyHiddenValues: items.slice(alwaysShowedCount),
      template: layout,
      ...fitmentFieldData,
      isCollapsed,
      toggleCollapsed,
      valuesCount: Fitments.length,
    }),
    rootRef,
  );
};

export default FitmentTable;
